/**
 * « Ma photo et mon pseudo » — ouvert d'un clic sur son profil (en haut à
 * droite). Kayla, 07-10-2026 : « j'ai pas le truc, sur Cockpit, pour modifier
 * ma photo de profil ou mon pseudo quand je clique dessus ».
 *
 * Le même panneau à droite que « Personnaliser » et que l'accueil, avec le
 * même bloc (`EtapeProfil`) : une seule façon de choisir sa photo et son
 * pseudo dans tout le cockpit. La photo s'enregistre au clic ; le pseudo, au
 * bouton.
 *
 * 🪪 Enregistrer stampe aussi `accueilTermineAt` (`terminerMonAccueil`,
 * idempotente) : c'est ce drapeau qui dit au badge « il a CHOISI, montre son
 * choix » au lieu de préférer le pseudo Discord (cf UserBadge). Sans lui, un
 * pseudo changé ici ne se voyait nulle part en haut à droite.
 */
import { useAction, useMutation, useQuery } from "convex/react";
import { useEffect, useState } from "react";

import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { EtapeProfil } from "@/core/auth/accueil/AccueilDuChatteur";
import { useSession } from "@/core/auth/useSession";
import { Button } from "@/shared/ui";
import { Check, Loader2 } from "@/shared/ui/icons";

import { PanneauLateral } from "./PanneauLateral";

export function PanneauProfil({
  accountId,
  fermer,
}: {
  accountId: Id<"accounts"> | null;
  fermer: () => void;
}) {
  const { user } = useSession();
  const me = useQuery(api.users.me);
  const gate = useQuery(api.members.myProfileGate, accountId ? { accountId } : "skip");
  const updateName = useMutation(api.users.updateName);
  const uploadAvatar = useAction(api.users.uploadAvatar);
  const clearAvatar = useAction(api.users.clearAvatar);
  const prendreMaPhotoDiscord = useAction(api.users.prendreMaPhotoDiscord);
  const terminerMonAccueil = useMutation(api.members.terminerMonAccueil);

  const nomActuel = me?.name ?? "";
  const discordPseudo = gate?.discordUsername ?? null;

  const [choix, setChoix] = useState<"discord" | "libre">("libre");
  const [pseudoLibre, setPseudoLibre] = useState("");
  // Une fois la fiche chargée : on part de ce qui est affiché aujourd'hui.
  const [pret, setPret] = useState(false);
  useEffect(() => {
    if (pret || me === undefined) return;
    setPseudoLibre(nomActuel);
    setChoix(discordPseudo && discordPseudo === nomActuel ? "discord" : "libre");
    setPret(true);
  }, [pret, me, nomActuel, discordPseudo]);

  const pseudo = (choix === "discord" && discordPseudo ? discordPseudo : pseudoLibre).trim();

  const [envoi, setEnvoi] = useState<
    { etat: "repos" | "envoi" | "fait" } | { etat: "erreur"; message: string }
  >({ etat: "repos" });

  async function enregistrer() {
    if (!pseudo || envoi.etat === "envoi") return;
    setEnvoi({ etat: "envoi" });
    try {
      if (pseudo !== nomActuel) await updateName({ name: pseudo });
      if (accountId) await terminerMonAccueil({ accountId });
      setEnvoi({ etat: "fait" });
    } catch (err) {
      setEnvoi({ etat: "erreur", message: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <PanneauLateral
      titre="Ma photo et mon pseudo"
      fermer={fermer}
      pied={
        <>
          {envoi.etat === "erreur" && (
            <p role="alert" className="mb-2 text-[12px] text-erreur">{envoi.message}</p>
          )}
          <Button
            variant="accent"
            className="w-full"
            disabled={!pseudo || envoi.etat === "envoi"}
            onClick={() => void enregistrer()}
            icon={
              envoi.etat === "envoi" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )
            }
          >
            {envoi.etat === "fait" ? "Enregistré" : "Enregistrer"}
          </Button>
        </>
      }
    >
      <EtapeProfil
        profil={{
          nomActuel,
          photoUrl: me?.avatarUrl ?? user?.profilePictureUrl ?? null,
          photoPersonnelle: me?.hasCustomAvatar ?? false,
          importerPhoto: async (fichier) => {
            if (!fichier.type.startsWith("image/")) throw new Error("Ce fichier n'est pas une image");
            await uploadAvatar({ bytes: await fichier.arrayBuffer(), contentType: fichier.type });
          },
          prendrePhotoDiscord: async () => {
            if (accountId) await prendreMaPhotoDiscord({ accountId });
          },
          retirerPhoto: async () => {
            await clearAvatar();
          },
        }}
        discordPseudo={discordPseudo}
        discordAvatarUrl={gate?.discordAvatarUrl ?? null}
        choix={choix}
        setChoix={(c) => {
          setChoix(c);
          setEnvoi({ etat: "repos" });
        }}
        pseudoLibre={pseudoLibre}
        setPseudoLibre={(v) => {
          setPseudoLibre(v);
          setEnvoi({ etat: "repos" });
        }}
        pseudo={pseudo}
      />
    </PanneauLateral>
  );
}
